import React, { useState, useEffect } from "react";
import { getContractObligations, calculateRiskScore, OBLIGATION_STATUSES, OBLIGATION_PRIORITIES, RISK_LEVELS } from "../../services/obligationService";
import { useApp } from "../../contexts/AppContext";
import { ObligationForm } from "./ObligationForm";
import { ObligationDetail } from "./ObligationDetail";

const statusColors = {
  "Not Started": "slate-400",
  "In Progress": "blue-600",
  "On Track": "green-700",
  "At Risk": "orange-600",
  "Completed": "green-700",
  "Overdue": "red-700",
  "Waived": "slate-500",
};

const statusBgColors = {
  "Not Started": "slate-400/20",
  "In Progress": "blue-600/20",
  "On Track": "green-700/20",
  "At Risk": "orange-600/20",
  "Completed": "green-700/20",
  "Overdue": "red-700/20",
  "Waived": "slate-500/20",
};

const priorityColors = {
  "Critical": "red-700",
  "High": "orange-600",
  "Medium": "yellow-600",
  "Low": "green-700",
};

const priorityBgColors = {
  "Critical": "red-700/20",
  "High": "orange-600/20",
  "Medium": "yellow-600/20",
  "Low": "green-700/20",
};

const riskColors = {
  "Low": "green-700",
  "Medium": "yellow-600",
  "High": "orange-600",
  "Critical": "red-700",
};

const riskBgColors = {
  "Low": "green-700/20",
  "Medium": "yellow-600/20",
  "High": "orange-600/20",
  "Critical": "red-700/20",
};

const getPriorityBadgeClass = (priority) => {
  if (priority === "Critical") return "bg-red-700/20 text-red-700";
  if (priority === "High") return "bg-orange-600/20 text-orange-600";
  if (priority === "Medium") return "bg-yellow-600/20 text-yellow-600";
  if (priority === "Low") return "bg-green-700/20 text-green-700";
  return "bg-slate-400/20 text-slate-400";
};

const getStatusBadgeClass = (status) => {
  if (status === "Overdue") return "bg-red-700/20 text-red-700";
  if (status === "At Risk") return "bg-orange-600/20 text-orange-600";
  if (status === "In Progress") return "bg-blue-600/20 text-blue-600";
  if (status === "On Track" || status === "Completed") return "bg-green-700/20 text-green-700";
  if (status === "Waived") return "bg-slate-500/20 text-slate-500";
  return "bg-slate-400/20 text-slate-400";
};

const getRiskBadgeClass = (riskLevel) => {
  if (riskLevel === "Critical") return "bg-red-700/20 border-red-700 text-red-700";
  if (riskLevel === "High") return "bg-orange-600/20 border-orange-600 text-orange-600";
  if (riskLevel === "Medium") return "bg-yellow-600/20 border-yellow-600 text-yellow-600";
  if (riskLevel === "Low") return "bg-green-700/20 border-green-700 text-green-700";
  return "bg-slate-400/20 border-slate-400 text-slate-400";
};

const getStatusBarColor = (status, percentage) => {
  if (percentage === 100) return "bg-green-700";
  if (status === "Overdue") return "bg-red-700";
  if (status === "At Risk") return "bg-orange-600";
  if (status === "In Progress") return "bg-blue-600";
  if (status === "On Track") return "bg-green-700";
  return "bg-slate-400";
};

const getRiskTextColor = (riskLevel) => {
  if (riskLevel === "Critical") return "text-red-700";
  if (riskLevel === "High") return "text-orange-600";
  if (riskLevel === "Medium") return "text-yellow-600";
  if (riskLevel === "Low") return "text-green-700";
  return "text-slate-400";
};

export const ObligationsList = ({ contractId, contractRefNo, canEdit, isAdmin }) => {
  const { service, addNotification } = useApp();
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "" });

  useEffect(() => {
    loadObligations();
  }, [service, contractId]);

  const loadObligations = async () => {
    try {
      setLoading(true);
      const data = await getContractObligations(service, contractId);
      setObligations(data);
    } catch (e) {
      console.error("Error loading obligations:", e);
      addNotification("Failed to load obligations", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = obligations.filter(o => {
    const matchStatus = !filters.status || o.Status === filters.status;
    const matchPriority = !filters.priority || o.Priority === filters.priority;
    return matchStatus && matchPriority;
  });

  const stats = {
    total: obligations.length,
    onTrack: obligations.filter(o => o.Status === "On Track").length,
    atRisk: obligations.filter(o => o.Status === "At Risk").length,
    overdue: obligations.filter(o => o.Status === "Overdue").length,
  };

  if (loading) {
    return <div className="p-6 text-center text-slate-400">Loading obligations...</div>;
  }

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-5">
        <h3 className="m-0 text-slate-100 text-lg font-semibold">Contract Obligations</h3>
        {(canEdit || isAdmin) && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-yellow-600 border-none rounded text-slate-900 cursor-pointer font-semibold text-xs hover:bg-yellow-500"
          >
            + Add Obligation
          </button>
        )}
      </div>

      {/* Stat Cards */}
      {obligations.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 uppercase font-semibold mb-1">Total</div>
            <div className="text-2xl font-semibold text-yellow-600">{stats.total}</div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 uppercase font-semibold mb-1">On Track</div>
            <div className="text-2xl font-semibold text-green-700">{stats.onTrack}</div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 uppercase font-semibold mb-1">At Risk</div>
            <div className="text-2xl font-semibold text-orange-600">{stats.atRisk}</div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 uppercase font-semibold mb-1">Overdue</div>
            <div className="text-2xl font-semibold text-red-700">{stats.overdue}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      {obligations.length > 0 && (
        <div className="flex gap-2 mb-4">
          <select
            value={filters.status}
            onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}
            className="px-2.5 py-1.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600"
          >
            <option value="">All Statuses</option>
            {OBLIGATION_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select
            value={filters.priority}
            onChange={e => setFilters(p => ({ ...p, priority: e.target.value }))}
            className="px-2.5 py-1.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600"
          >
            <option value="">All Priorities</option>
            {OBLIGATION_PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
      )}

      {/* List */}
      <div className="grid gap-3">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-slate-400 bg-slate-800 rounded-lg">
            {obligations.length === 0
              ? "No obligations yet. " + ((canEdit || isAdmin) ? "Click + Add to create one." : "")
              : "No obligations match the filters."}
          </div>
        ) : (
          filtered.map(obligation => {
            const riskScore = calculateRiskScore(obligation);
            const dueDate = new Date(obligation.DueDate);
            const today = new Date();
            const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

            return (
              <div
                key={obligation.id}
                onClick={() => setSelectedObligation(obligation)}
                className="bg-slate-800 border border-slate-700 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:bg-slate-700"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-base font-semibold text-slate-100">{obligation.Title}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getPriorityBadgeClass(obligation.Priority)}`}>
                        {obligation.Priority}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadgeClass(obligation.Status)}`}>
                        {obligation.Status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-sm text-slate-400">
                      <div>
                        <span className="font-semibold">Category:</span> {obligation.Category}
                      </div>
                      <div>
                        <span className="font-semibold">Responsible:</span> {obligation.ResponsibleParty}
                      </div>
                      <div>
                        <span className="font-semibold">Due:</span> {dueDate.toLocaleDateString("en-GB")} ({daysUntilDue}d)
                      </div>
                      <div>
                        <span className="font-semibold">Risk:</span> <span className={getRiskTextColor(obligation.RiskLevel)}>{obligation.RiskLevel}</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="text-xs text-slate-400 mb-1">Progress: {obligation.ProgressPercentage}%</div>
                      <div className="bg-slate-900 h-1.5 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${getStatusBarColor(obligation.Status, obligation.ProgressPercentage)}`}
                          style={{width: obligation.ProgressPercentage + "%"}}
                        />
                      </div>
                    </div>

                    {obligation.Description && (
                      <p className="mt-2 text-sm text-slate-400 leading-snug">
                        {obligation.Description}
                      </p>
                    )}
                  </div>

                  {riskScore.score >= 50 && (
                    <div className={`${getRiskBadgeClass(obligation.RiskLevel)} border rounded p-2 text-center flex-shrink-0`}>
                      <div className="text-xs text-slate-400 font-semibold">RISK SCORE</div>
                      <div className={`text-xl font-semibold ${getRiskTextColor(obligation.RiskLevel)}`}>{riskScore.score}</div>
                      <div className={`text-xs ${getRiskTextColor(obligation.RiskLevel)} mt-1`}>{riskScore.trend}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <ObligationForm
          contractId={contractId}
          contractRefNo={contractRefNo}
          onSave={() => {
            setShowForm(false);
            loadObligations();
          }}
          onCancel={() => setShowForm(false)}
          service={service}
        />
      )}

      {/* Detail Modal */}
      {selectedObligation && (
        <ObligationDetail
          obligation={selectedObligation}
          onClose={() => setSelectedObligation(null)}
          onUpdate={() => {
            setSelectedObligation(null);
            loadObligations();
          }}
          canEdit={canEdit || isAdmin}
        />
      )}
    </div>
  );
};
